import React, { useMemo, useState, useCallback } from 'react';
import { Box, TextField } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import Section from '../../../layout/Section';
import HorizontalTable, { HorizontalTableRow } from '../../../tables/HorizontalTable';
import { YearlyCostsResponse, OpexItem } from '../../../../types';
import { formatCurrency } from '../../../../utils/formatters';
import { fonts } from '../../../../styles/theme';

/**
 * Editierbare Zelle mit lokalem State
 * Speichert nur beim Verlassen der Zelle (onBlur)
 */
interface EditableCellProps {
    initialValue: number;
    itemId: string;
    jahr: number;
    onSave: (itemId: string, jahr: number, wert: number) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({
    initialValue,
    itemId,
    jahr,
    onSave,
}) => {
    const [localValue, setLocalValue] = useState(initialValue);
    const [hasChanged, setHasChanged] = useState(false);

    const handleBlur = useCallback(() => {
        if (hasChanged && localValue !== initialValue) {
            onSave(itemId, jahr, localValue);
        }
        setHasChanged(false);
    }, [hasChanged, localValue, initialValue, itemId, jahr, onSave]);

    return (
        <NumericFormat
            value={localValue}
            thousandSeparator="."
            decimalSeparator=","
            allowNegative={false}
            decimalScale={2}
            suffix=" €"
            customInput={TextField}
            size="small"
            onValueChange={(values) => {
                const newValue = values.floatValue || 0;
                setLocalValue(newValue);
                setHasChanged(true);
            }}
            onBlur={handleBlur}
            sx={{
                width: '100%',
                '& .MuiInputBase-input': {
                    textAlign: 'right',
                    fontSize: '0.875rem',
                    fontFamily: fonts.monospace,
                    padding: '2px 4px',
                },
            }}
        />
    );
};

interface YearlyCostsSectionProps {
    data: YearlyCostsResponse | null;
    isLoading?: boolean;
    canEdit: boolean;
    /** Callback für Jahreswert-Änderungen (itemId, jahr, wert) */
    onYearlyValueChange?: (itemId: string, jahr: number, wert: number) => void;
    /** OPEX Items für Bezeichnungen - optional, für Item-basiertes Modell */
    opexItems?: OpexItem[];
}

/**
 * YearlyCostsSection - Jährliche Kostenübersicht
 * 
 * Zeigt eine Tabelle mit Jahren als Spalten.
 * Zeilen: Alle OPEX-Positionen + Summe + Abschreibungen
 * Bei Modus "manuell": Zellen sind editierbar
 */
const YearlyCostsSection: React.FC<YearlyCostsSectionProps> = ({
    data,
    isLoading,
    canEdit,
    onYearlyValueChange,
    opexItems,
}) => {
    // Sortiere Daten nach Jahr
    const sortedYears = useMemo(() => {
        if (!data?.jahre) return [];
        return [...data.jahre].sort((a, b) => a.jahr - b.jahr);
    }, [data?.jahre]);

    // Spaltenüberschriften (Jahre)
    const columnHeaders = useMemo(() =>
        sortedYears.map(y => y.jahr),
        [sortedYears]
    );

    // Item-ID zu Bezeichnung Mapping
    const itemLabels = useMemo(() => {
        const labels: Record<string, string> = {};
        if (opexItems) {
            for (const item of opexItems) {
                labels[item.id] = item.bezeichnung;
            }
        }
        return labels;
    }, [opexItems]);

    // Ermittle alle einzigartigen Kosten-Keys aus den Jahren
    const allCostKeys = useMemo(() => {
        const keys = new Set<string>();
        for (const year of sortedYears) {
            for (const key of Object.keys(year.kosten)) {
                keys.add(key);
            }
        }
        return Array.from(keys);
    }, [sortedYears]);

    // Zeilen für die Tabelle generieren
    const rows: HorizontalTableRow[] = useMemo(() => {
        if (!sortedYears.length) return [];

        const result: HorizontalTableRow[] = [];

        // Zeilen für jeden Kosten-Key (Item-ID oder alter CostType)
        for (const costKey of allCostKeys) {
            const values = sortedYears.map(year => year.kosten[costKey] || 0);
            const total = values.reduce((sum, v) => sum + v, 0);

            // Label: Entweder aus opexItems oder den Key selbst verwenden
            const label = itemLabels[costKey] || costKey;

            result.push({
                key: costKey,
                label,
                values,
                lastColumnValue: total,
            });
        }

        // Summe Betriebskosten (highlighted)
        const summeValues = sortedYears.map(year => year.summe_betriebskosten);
        const summeTotal = summeValues.reduce((sum, v) => sum + v, 0);
        result.push({
            key: 'summe_betriebskosten',
            label: 'Summe Betriebskosten',
            values: summeValues,
            lastColumnValue: summeTotal,
            highlightRow: true,
        });

        // Abschreibungen (highlighted)
        const afaValues = sortedYears.map(year => year.abschreibungen);
        const afaTotal = afaValues.reduce((sum, v) => sum + v, 0);
        result.push({
            key: 'abschreibungen',
            label: 'Abschreibungen (AfA)',
            values: afaValues,
            lastColumnValue: afaTotal,
            highlightRow: true,
        });

        return result;
    }, [sortedYears, allCostKeys, itemLabels]);

    // Editierbare Zellen (für Modus "manuell")
    const editableMap = useMemo(() => {
        const map: Record<string, Record<number, boolean>> = {};
        for (const year of sortedYears) {
            for (const [costKey, isEditable] of Object.entries(year.is_editable)) {
                if (!map[costKey]) map[costKey] = {};
                map[costKey][year.jahr] = isEditable;
            }
        }
        return map;
    }, [sortedYears]);

    // Custom Cell Renderer für editierbare Zellen
    const renderEditableCell = useCallback((
        itemId: string,
        jahr: number,
        value: number
    ) => {
        const isEditable = editableMap[itemId]?.[jahr] && canEdit;

        if (!isEditable) {
            return formatCurrency(value);
        }

        return (
            <EditableCell
                key={`${itemId}-${jahr}`}
                initialValue={value}
                itemId={itemId}
                jahr={jahr}
                onSave={onYearlyValueChange!}
            />
        );
    }, [editableMap, canEdit, onYearlyValueChange]);

    if (isLoading) {
        return (
            <Section title="Jährliche Kostenübersicht" noPadding>
                <Box sx={{ p: 2, textAlign: 'center' }}>Lade Daten...</Box>
            </Section>
        );
    }

    if (!data || !sortedYears.length) {
        return (
            <Section title="Jährliche Kostenübersicht" noPadding>
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                    Keine Kostendaten verfügbar. Bitte zunächst OPEX-Daten eingeben.
                </Box>
            </Section>
        );
    }

    return (
        <Section title="Jährliche Kostenübersicht" noPadding>
            <HorizontalTable
                headerLabel=""
                columnHeaders={columnHeaders}
                rows={rows}
                lastColumn={{ header: 'Summe' }}
                labelColumnWidth={200}
                dataColumnWidth={110}
                renderCell={(rowKey, columnHeader, value) =>
                    renderEditableCell(rowKey, columnHeader as number, (value as number) || 0)
                }
            />
        </Section>
    );
};

export default React.memo(YearlyCostsSection);
